(() => {
  'use strict';
  const stateKey = '__lasgymScrollEntry';
  const storageKey = 'lasgym:scroll-positions:v1';
  let entries = {};
  const navigationType = performance.getEntriesByType('navigation')[0]?.type;
  let restoring = navigationType === 'back_forward' || navigationType === 'reload';
  let leaving = false;
  let timer;
  let retry;
  let anchor = null;
  let initialRestoreAttempted = false;
  let pageUrl = location.href;
  try { entries = JSON.parse(sessionStorage.getItem(storageKey) || '{}'); } catch (_) {}
  if (!entries || typeof entries !== 'object' || Array.isArray(entries)) entries = {};
  if ('scrollRestoration' in history) history.scrollRestoration = 'auto';

  function entryId() {
    const current = history.state;
    if (current && current[stateKey]) return current[stateKey];
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2);
    try { history.replaceState({ ...(current || {}), [stateKey]: id }, '', location.href); } catch (_) {}
    return id;
  }

  let id = entryId();

  function refreshEntries() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      if (stored && typeof stored === 'object' && !Array.isArray(stored)) entries = { ...entries, ...stored };
    } catch (_) {}
  }

  function rememberLink(link) {
    const scope = link.closest('[id^="shopify-section-"], [id^="section-"], main[id]');
    const root = scope || document;
    const href = link.getAttribute('href');
    const matches = [...root.querySelectorAll('a[href]')].filter(item => item.getAttribute('href') === href);
    return { scope: scope?.id || '', href, index: matches.indexOf(link), top: link.getBoundingClientRect().top };
  }

  function save() {
    if (restoring || leaving || location.href !== pageUrl) return;
    id = entryId();
    refreshEntries();
    entries[id] = {
      url: pageUrl, x: scrollX, y: scrollY, width: innerWidth, anchor,
      carousels: [...document.querySelectorAll('[data-carousel-config]')].map((element, index) => ({
        index, slide: element.swiper?.realIndex ?? null
      })).filter(item => item.slide !== null),
      time: Date.now()
    };
    const keys = Object.keys(entries).sort((a, b) => entries[b].time - entries[a].time);
    keys.slice(40).forEach(key => delete entries[key]);
    try { sessionStorage.setItem(storageKey, JSON.stringify(entries)); } catch (_) {}
  }

  function stop() {
    clearTimeout(retry);
    restoring = false;
    document.removeEventListener('wheel', interrupt, true);
    document.removeEventListener('touchstart', interrupt, true);
    document.removeEventListener('pointerdown', interrupt, true);
    document.removeEventListener('keydown', interrupt, true);
  }

  function interrupt(event) {
    if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab'].includes(event.key)) return;
    stop();
    anchor = null;
  }

  function restore() {
    refreshEntries();
    const saved = entries[history.state?.[stateKey] || id];
    stop();
    if (!saved || saved.url !== location.href || !Number.isFinite(saved.y)) return;
    restoring = true;
    leaving = false;
    anchor = saved.anchor;
    const started = performance.now();
    const restoredCarousels = new WeakSet();
    ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(type => document.addEventListener(type, interrupt, { capture: true, passive: true }));
    function apply() {
      if (!restoring) return;
      const carousels = document.querySelectorAll('[data-carousel-config]');
      (saved.carousels || []).forEach(item => {
        const element = carousels[item.index];
        const swiper = element?.swiper;
        if (!swiper || swiper.destroyed || restoredCarousels.has(element)) return;
        if (swiper.params.loop) swiper.slideToLoop(item.slide, 0, false);
        else swiper.slideTo(item.slide, 0, false);
        restoredCarousels.add(element);
      });
      let y = saved.y;
      if (saved.anchor) {
        const scope = saved.anchor.scope ? document.getElementById(saved.anchor.scope) : document;
        const link = scope && [...scope.querySelectorAll('a[href]')].filter(item => item.getAttribute('href') === saved.anchor.href)[saved.anchor.index];
        if (link && link.getClientRects().length) y = scrollY + link.getBoundingClientRect().top - saved.anchor.top;
      }
      if (Math.abs(scrollY - y) > 1 || Math.abs(scrollX - saved.x) > 1) {
        window.scrollTo({ left: saved.x, top: Math.max(0, y), behavior: 'instant' });
      }
      if (performance.now() - started < 4000) retry = setTimeout(apply, 100);
      else { stop(); save(); }
    }
    apply();
  }

  document.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search && url.hash)) return;
    stop();
    anchor = rememberLink(link);
    save();
  }, true);

  window.addEventListener('scroll', () => {
    if (restoring || leaving) return;
    anchor = null;
    clearTimeout(timer);
    timer = setTimeout(save, 150);
  }, { passive: true });
  window.addEventListener('pagehide', () => { clearTimeout(timer); save(); leaving = true; stop(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') save(); });
  document.addEventListener('lasgym:before-filter', save);
  document.addEventListener('lasgym:after-filter', () => { pageUrl = location.href; id = entryId(); anchor = null; save(); });
  document.addEventListener('lasgym:history-reload', () => { leaving = true; stop(); });
  function restoreInitial() {
    if (initialRestoreAttempted) return;
    initialRestoreAttempted = true;
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation?.type === 'back_forward' || navigation?.type === 'reload') restore();
  }
  document.addEventListener('DOMContentLoaded', restoreInitial, { once: true });
  window.addEventListener('pageshow', event => {
    leaving = false;
    if (event.persisted) restore();
    else restoreInitial();
  });
})();
